import { render } from 'preact';
import type { Brand } from '@clawharbor/manifest';
import { copyText } from '../ui/copy.js';
import { ApiError, createDashboardApi } from './api.js';
import type { TenantDetail } from './api.js';
import { DashboardView } from './app.js';
import { hashFor, loginNoticeFor, parseRoute } from './route.js';
import type { Destination, TenantTab } from './route.js';
import {
  DashboardStore,
  DESCRIPTION_MIN,
  draftOf,
  initialOnboard,
  initialState,
  runOf,
  viewDraftOf,
} from './store.js';
import type { DashboardActions, DashboardState, Screen } from './store.js';
import { coerceArgs, fieldsOf, missingRequired } from './ui/schema.js';
import { applyTheme, storedTheme } from './ui/theme.js';

/**
 * Wiring for the dashboard.
 *
 * Four rules run through this file.
 *
 * WHO THIS PAGE IS, THE SERVER SAYS. The first request on load is
 * `GET /api/auth/me`, and its answer — an account, the operator, or nobody —
 * is the only thing that decides between the login screen and the sites. A
 * screen is never shown on the strength of a cookie this page cannot read, and
 * a 401 from any later call is the same fact arriving late: the session ended,
 * so the login screen comes back with a sentence saying so, and never a toast
 * over a list of sites the person is no longer allowed to see.
 *
 * THE SECRET lives in memory for the life of the page. It is the host's own
 * `CLAWHARBOR_RELOAD_SECRET` and it governs every tenant on the box, so it is
 * held on the api object and mirrored into state only to keep the password field
 * controlled. Nothing here writes it to `localStorage`, `sessionStorage`, a
 * cookie or a URL: a URL reaches browser history, a proxy log and a screenshot,
 * and there is no per-tenant scoping behind this value to limit what that costs.
 * An account's session is the server's httpOnly cookie, which this file never
 * sees and never stores; "Sign out" is a POST that asks the server to end it.
 *
 * NOTHING IS FABRICATED. Every number on these screens came out of a response;
 * where the response carried nothing, the screen says so in words. There is no
 * demo mode and no recorded fixture to fall back to, which is deliberate — the
 * onboarding console can replay a crawl because a crawl is a story, but a
 * dashboard that invented a site would be lying about the state of a live host.
 * The same rule keeps scope on the server: an account is shown exactly the
 * tenants the scoped routes return, and nothing here filters a list by the
 * membership the principal carries, because a client-side filter is a second
 * copy of an authorisation rule that would drift from the one that matters.
 *
 * A FAILED REQUEST IS AN ERROR ON THE SCREEN, never an empty table. An empty
 * Sites table reads as "no sites" and an empty traffic table reads as "no calls";
 * both are conclusions an operator would act on, and neither is what an
 * unreachable API means. Every loader below patches `error` and leaves the last
 * good data in place rather than clearing it into a blank grid.
 */

/**
 * The origin this page talks to.
 *
 * Mirrors `apiBase()` in api.ts — same meta tag, same `?api` override, same
 * precedence — because that function is private to the client and the header has
 * to name the host an operator is actually editing. Getting this wrong is
 * expensive in exactly one way: a person who believes they are on staging while
 * pointed at production unpublishes a live tenant.
 */
function apiOrigin(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="clawharbor-api"]')?.content?.trim();
  const override = new URLSearchParams(location.search).get('api');
  const base = (meta || override || '').replace(/\/+$/, '');
  if (!base) return location.origin;
  try {
    return new URL(base, location.origin).origin;
  } catch {
    return base;
  }
}

/**
 * The router lives in route.ts, which knows nothing about the DOM so that the
 * round trip between a route and its hash can be tested without a browser. What
 * stays here is everything that touches `location`: reading the hash, writing
 * it, and deciding what to load once it has changed.
 */

/* ── Shape adapters ────────────────────────────────────────────────────── */

/**
 * The tenant's brand, in the shape `BrandHead` reads.
 *
 * `apps/server/src/dashboard/service.ts` reports the palette as an array of
 * `{role, value, confidence, derived, evidence}` and the fonts as a list of
 * `"sans: <stack>"` strings, while the card was written against the manifest's
 * own `Brand` — a role-keyed map and a `typography.families.sans` object. Read
 * straight through, `sans.substitute` is a property access on `undefined` and the
 * whole tenant screen goes blank, which is the one failure mode this dashboard
 * must not have. So the response is translated here, at the edge, and both
 * shapes are accepted: a host that later sends the manifest brand verbatim keeps
 * working without a second adapter.
 */
function normalizeBrand(raw: unknown): Brand {
  const brand = (raw ?? {}) as Record<string, unknown>;
  const palette = brand['palette'];
  const typography = brand['typography'] as Record<string, unknown> | undefined;

  const roles: Record<string, unknown> = {};
  if (Array.isArray(palette)) {
    for (const entry of palette) {
      if (entry == null || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const role = typeof e['role'] === 'string' ? e['role'] : null;
      if (role) roles[role] = e;
    }
  } else if (palette != null && typeof palette === 'object') {
    Object.assign(roles, palette as Record<string, unknown>);
  }

  if (typography?.['families']) return { ...brand, palette: roles } as unknown as Brand;

  // `fontFamilies` is a list of "role: stack" strings; the first is the body
  // face. Reported as observed with no substitute, which is true of the string:
  // it says what the stack is and nothing about where the file came from.
  const families = Array.isArray(brand['fontFamilies']) ? (brand['fontFamilies'] as unknown[]) : [];
  const first = families.find((f) => typeof f === 'string') as string | undefined;
  const stack = first ? first.replace(/^[a-z]+:\s*/i, '') : 'system-ui, sans-serif';
  return {
    ...brand,
    palette: roles,
    typography: { families: { sans: { observed: stack, stack } } },
  } as unknown as Brand;
}

function withBrand(detail: TenantDetail): TenantDetail {
  return { ...detail, brand: normalizeBrand(detail.brand) };
}

function message(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/* ── Boot ──────────────────────────────────────────────────────────────── */

function boot(container: HTMLElement): void {
  const api = createDashboardApi();
  const store = new DashboardStore();
  const origin = apiOrigin();

  let copyTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set while the router itself is writing the hash, so it does not re-enter. */
  let navigating = false;

  /**
   * Move focus when the SCREEN changes, and only then.
   *
   * The dashboard replaces its main region without a navigation, so the browser
   * performs no focus reset of its own: an operator who activated a Sites row
   * with the keyboard would otherwise be left with focus on `<body>` and a
   * control that no longer exists, with no announcement that a page of new
   * content had arrived. The key includes the tenant id AND its tab, because
   * tenant → tenant and tab → tab are both screen changes even though
   * `state.screen` does not move.
   *
   * A repaint on a save, a poll or a run result must NOT steal focus — that
   * would yank the caret out of a description box mid-sentence — so this fires
   * on transition only, and the first paint is a page load the reader is already
   * at the top of.
   */
  let painted: string | null = null;
  const paint = (): void => {
    const state = store.state;
    const signedOut = !state.booting && state.principal === null;
    const key = signedOut
      ? 'login'
      : `${state.screen}:${state.tenantId ?? ''}:${state.screen === 'tenant' ? state.tenantTab : ''}`;
    render(DashboardView({ state, actions, now: Date.now(), origin }), container);
    const first = painted === null;
    if (key === painted) return;
    painted = key;
    if (first) return;
    // The login screen's destination is its field: you arrive there by signing
    // out or by a session ending, and the next thing you do is type an address.
    if (signedOut) {
      document.getElementById('login-email')?.focus();
      return;
    }
    requestAnimationFrame(() => {
      container.querySelector<HTMLElement>('[data-focus-target]')?.focus({ preventScroll: true });
    });
  };

  /* ── Signing out, in all its forms ───────────────────────────────────── */

  /**
   * Back to the login screen, with a sentence about why.
   *
   * Everything the principal bought goes with it: no site list left painted behind
   * a login form, no half-typed description belonging to a tenant this page can
   * no longer read, and no operator header on the next request. The hash is
   * left alone, so signing back in lands where the person was.
   */
  function signedOut(notice: string | null): void {
    api.secret = '';
    stopWatching();
    store.patch({ ...initialState(), booting: false, principal: null, loginNotice: notice });
    store.patch({ screen: 'login' });
  }

  /**
   * A 401 from any guarded route is the session ending, not a failure of the
   * screen that asked. It is rendered as the login screen with a sentence, and
   * never as an error note or a toast: the screen behind such a toast is one the
   * server has just refused to show, so leaving it on the screen would be
   * showing stale data as current.
   */
  function unauthorized(err: unknown): boolean {
    if (!(err instanceof ApiError) || err.status !== 401) return false;
    signedOut(
      store.state.principal?.kind === 'operator'
        ? 'The host stopped accepting the secret held on this page. Enter it again to continue as the operator.'
        : 'Your session ended. Ask for a new sign-in link to continue.',
    );
    return true;
  }

  /** A loader's failure: the login screen on a 401, the error on the screen otherwise. */
  function failed(err: unknown): void {
    if (unauthorized(err)) return;
    store.patch({ loading: false, error: message(err) });
  }

  /* ── Loaders ─────────────────────────────────────────────────────────── */

  async function loadSites(): Promise<void> {
    store.patch({ loading: true, error: null });
    try {
      const overview = await api.overview();
      // `/overview` reports the registry, the failures and the monitor, but no
      // usage window; the Sites header states a call total, and a zero printed
      // where nothing was reported is the kind of number an operator believes.
      // So the window is read from the usage route in the same breath.
      const usage = await api.usage(store.state.usageDays, null);
      store.patch({ overview: { ...overview, usage }, loading: false, error: null });
      // An account with nothing published gets the first-run screen, and that
      // screen has one job the overview cannot answer: whether this person
      // already started a crawl and walked away. Asked only in that case, so
      // the ordinary Sites load stays two requests.
      if (overview.tenants.length === 0 && store.state.principal?.kind === 'account') {
        void loadJobs();
      }
    } catch (err) {
      failed(err);
    }
  }

  async function loadTenant(tenantId: string): Promise<void> {
    if (store.state.tenantId !== tenantId) {
      store.clearTenantWork();
      store.patch({ tenant: null });
    }
    store.patch({ tenantId, loading: true, error: null });
    try {
      const tenant = await api.tenant(tenantId);
      store.patch({ tenant: withBrand(tenant), loading: false, error: null });
    } catch (err) {
      failed(err);
    }
  }

  async function loadUsage(): Promise<void> {
    store.patch({ loading: true, error: null });
    try {
      const usage = await api.usage(store.state.usageDays, store.state.usageTenantId);
      // The site picker on that screen is drawn from the overview, so the two
      // reads happen together rather than leaving an empty <select>.
      if (!store.state.overview) {
        const overview = await api.overview();
        store.patch({ overview: { ...overview, usage } });
      }
      store.patch({ usage, loading: false, error: null });
    } catch (err) {
      failed(err);
    }
  }

  /**
   * The account's second factor, for the Settings screen.
   *
   * A failure here is kept inside the security block rather than raised as the
   * screen's error: the billing section beside it may have loaded fine, and a
   * host built before two-step sign-in existed answers 404, which the panel
   * states as "not available on this host" rather than as an outage.
   */
  async function loadSecurity(): Promise<void> {
    try {
      const twoFactor = await api.twoFactor();
      patchSecurity({ twoFactor, error: null });
    } catch (err) {
      if (unauthorized(err)) return;
      patchSecurity({
        error: err instanceof ApiError && err.status === 404 ? 'unavailable' : message(err),
      });
    }
  }

  function patchSecurity(next: Partial<DashboardState['security']>): void {
    store.patch({ security: { ...store.state.security, ...next } });
  }

  /** Runs one security request with its button marked busy and its failure kept on the panel. */
  function securityCall<T>(
    busy: NonNullable<DashboardState['security']['busy']>,
    call: () => Promise<T>,
    done: (value: T) => void,
  ): void {
    if (store.state.security.busy) return;
    patchSecurity({ busy, error: null });
    call()
      .then((value) => {
        patchSecurity({ busy: null });
        done(value);
      })
      .catch((err: unknown) => {
        if (unauthorized(err)) return;
        patchSecurity({ busy: null, error: message(err) });
      });
  }

  async function loadBilling(): Promise<void> {
    store.patch({ loading: true, error: null, billingError: null });
    try {
      // `'disabled'` is the 503 a host with no Stripe key gives, and it is kept
      // as an answer rather than an error: the screen renders it as "not
      // configured" with dead buttons, which is the truth about that host.
      const billing = await api.billing();
      store.patch({ billing, loading: false, error: null });
    } catch (err) {
      failed(err);
    }
  }

  /* ── Onboarding: the poll ────────────────────────────────────────────── */

  /**
   * A crawl is watched by polling `GET /api/onboard/:id` every few seconds
   * rather than by the SSE stream the public console uses. The stream is the
   * better transport for a log that scrolls; this screen shows a phase, a
   * percentage and the last lines, and a poll is one code path with no
   * reconnect logic, no `Last-Event-ID`, and nothing to get wrong behind a
   * proxy that buffers. The record it reads is the durable one the stream is a
   * shortcut over, so nothing is lost by reading it late.
   */
  const POLL_MS = 2500;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  /** The job being polled, or null. Checked after every await so a stale tick cannot patch. */
  let watching: string | null = null;

  function stopWatching(): void {
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
    watching = null;
  }

  async function loadDraft(jobId: string): Promise<void> {
    try {
      const draft = await api.draftSummary(jobId);
      if (store.state.onboard.jobId !== jobId) return;
      store.patchOnboard({ draft, draftError: null });
    } catch (err) {
      if (unauthorized(err)) return;
      if (store.state.onboard.jobId !== jobId) return;
      // A succeeded job with no readable draft is a real state the server
      // names (`no_manifest`, `draft_invalid`), and it is shown as the reason
      // there is no Publish button — not as a button that would fail.
      store.patchOnboard({ draft: null, draftError: message(err) });
    }
  }

  function watchJob(jobId: string): void {
    stopWatching();
    watching = jobId;
    const tick = async (): Promise<void> => {
      if (watching !== jobId) return;
      try {
        const job = await api.onboardJob(jobId);
        if (watching !== jobId) return;
        store.patchOnboard({ job, jobError: null });
        if (job.state === 'succeeded') {
          stopWatching();
          await loadDraft(jobId);
          return;
        }
        if (job.state === 'failed' || job.state === 'cancelled') {
          stopWatching();
          return;
        }
      } catch (err) {
        if (unauthorized(err)) {
          stopWatching();
          return;
        }
        if (watching !== jobId) return;
        store.patchOnboard({ jobError: message(err) });
        // An unknown id will not become known by asking again. Anything else —
        // a blip, a restart — is retried, with the error left on the screen
        // until an answer replaces it.
        if (err instanceof ApiError && err.status === 404) {
          stopWatching();
          return;
        }
      }
      if (watching !== jobId) return;
      pollTimer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
  }

  async function loadJobs(): Promise<void> {
    try {
      const jobs = await api.onboardJobs();
      store.patchOnboard({ jobs, jobsError: null });
    } catch (err) {
      if (unauthorized(err)) return;
      store.patchOnboard({ jobsError: message(err) });
    }
  }

  /**
   * The screen reads two things: the recent list, always, and the job in the
   * hash when there is one. A job id that differs from the one on the screen
   * drops the whole block first, so the previous job's draft can never sit
   * under this job's progress bar.
   */
  function loadOnboard(jobId: string | null): void {
    if (jobId !== store.state.onboard.jobId) {
      stopWatching();
      const { url, ownership } = store.state.onboard;
      store.patchOnboard({ ...initialOnboard(), url, ownership, jobId });
    }
    store.patch({ loading: false, error: null });
    void loadJobs();
    if (jobId) watchJob(jobId);
  }

  function loadFor(screen: Screen): void {
    if (!store.state.principal) return;
    // Sites and the host screen read the same overview; the second is that
    // read with the operator's half of it rendered instead of the customer's.
    if (screen === 'sites' || screen === 'host') void loadSites();
    else if (screen === 'overview') {
      // The front page reads what Sites reads, plus the crawl list: a crawl
      // somebody started and walked away from is the one piece of unfinished
      // business the overview exists to put back in front of them.
      void loadSites();
      if (store.state.principal?.kind === 'account') void loadJobs();
    }
    else if (screen === 'tenant' && store.state.tenantId) void loadTenant(store.state.tenantId);
    else if (screen === 'crawl') loadOnboard(store.state.onboard.jobId);
    else if (screen === 'traffic') void loadUsage();
    else if (screen === 'settings') {
      void loadBilling();
      if (store.state.principal?.kind === 'account') void loadSecurity();
    }
  }

  /** Applies the hash to the store and loads whatever that screen reads. */
  function applyRoute(): void {
    const route = parseRoute(location.hash, store.state.usageDays);
    if (store.state.booting) return;
    if (!store.state.principal) {
      // The destination is kept in the hash so signing in lands on it; only the
      // screen is forced, because there is nothing to show without a principal.
      // `#/login?error=…` is the one hash with something to say to this screen.
      store.patch({
        screen: 'login',
        ...(route.loginError ? { loginNotice: loginNoticeFor(route.loginError) } : {}),
      });
      return;
    }
    if (route.screen === 'login') {
      // A signed-in person on `#/login` — the callback's landing after the
      // cookie was set, or a bookmark — belongs on the front page.
      navigate({ screen: 'overview' });
      return;
    }
    if (route.screen === 'host' && store.state.principal.kind !== 'operator') {
      // The host screen reports other people's sites. An account that reached
      // it from a bookmark is sent to its own, rather than shown a screen whose
      // every read the server would refuse anyway.
      navigate({ screen: 'overview' });
      return;
    }
    const changedTenant = route.screen === 'tenant' && route.tenantId !== store.state.tenantId;
    store.patch({
      screen: route.screen,
      tenantId: route.screen === 'tenant' ? route.tenantId : store.state.tenantId,
      tenantTab: route.tenantTab,
      usageDays: route.usageDays,
      usageTenantId: route.usageTenantId,
      notice: null,
    });
    if (changedTenant) store.clearTenantWork();
    // A tab change within one tenant is the same payload sliced differently —
    // it must not re-fetch, and it must not drop what the person has typed.
    if (route.screen === 'tenant' && !changedTenant && store.state.tenant) return;
    if (route.screen === 'crawl') {
      // The hash is the job, so the block is aligned to it before the loader
      // runs; the loader drops the block itself when the id moved.
      loadOnboard(route.jobId);
      return;
    }
    // Leaving the screen ends the poll. The job keeps running on the server;
    // the recent list and `#/crawl/<id>` are how it is found again.
    stopWatching();
    loadFor(route.screen);
  }

  function navigate(to: Destination): void {
    const next = hashFor(to);
    if (location.hash === next) {
      applyRoute();
      return;
    }
    navigating = true;
    location.hash = next;
    navigating = false;
    applyRoute();
  }

  window.addEventListener('hashchange', () => {
    if (navigating) return;
    applyRoute();
  });

  /* ── Actions ─────────────────────────────────────────────────────────── */

  const tenantId = (): string | null => store.state.tenantId;

  /** Reloads the tenant after a mutation. The screen shows server state, not ours. */
  async function refreshTenant(): Promise<void> {
    const id = tenantId();
    if (!id) return;
    try {
      const tenant = await api.tenant(id);
      store.patch({ tenant: withBrand(tenant) });
    } catch (err) {
      if (unauthorized(err)) return;
      store.patch({ error: message(err) });
    }
  }

  async function patchTool(tool: string, patch: Parameters<typeof api.patchTool>[2]): Promise<void> {
    const id = tenantId();
    if (!id) return;
    store.patchMap('savingTool', tool, true);
    store.patchMap('toolErrors', tool, null);
    try {
      await api.patchTool(id, tool, patch);
      await refreshTenant();
      store.patch({ notice: `Saved ${tool}.` });
    } catch (err) {
      if (unauthorized(err)) return;
      store.patchMap('toolErrors', tool, message(err));
    } finally {
      store.patchMap('savingTool', tool, null);
    }
  }

  /**
   * Lands on whatever the hash asked for once a principal exists — the screen
   * a reload interrupted, or the page a link pointed at, rather than the front
   * page every time. A hash naming the login screen itself goes to Overview.
   */
  function landAfterSignIn(): void {
    const route = parseRoute(location.hash, store.state.usageDays);
    navigate(route.screen === 'login' ? { screen: 'overview' } : route);
  }

  /** A well-formed address, by the same rule the input's `type="email"` applies. */
  function wellFormed(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  const actions: DashboardActions = {
    setEmail(value) {
      store.patch({ email: value, loginError: null });
    },

    sendLink() {
      const email = store.state.email.trim();
      if (!email || store.state.sending) return;
      if (!wellFormed(email)) {
        store.patch({ loginError: 'That does not look like an email address.' });
        return;
      }
      store.patch({ sending: true, loginError: null, loginNotice: null });
      void api
        .login(email)
        .then((receipt) => {
          // `sent` is what the screen renders and the server says it for every
          // well-formed address; the screen's sentence is the same whether the
          // address has an account or not, and that is the point of it.
          store.patch({ sending: false, sent: receipt.sent, devLink: receipt.devLink });
        })
        .catch((err: unknown) => {
          store.patch({ sending: false, loginError: message(err) });
        });
    },

    resetLogin() {
      store.patch({ sent: false, devLink: null, loginError: null });
    },

    signOut() {
      // The cookie is the server's to clear; the request is what clears it.
      // The page forgets its own state whatever the answer, because a person
      // who pressed "Sign out" and still sees their sites has not been signed out.
      void api
        .logout()
        .catch(() => undefined)
        .then(() => signedOut('You are signed out.'));
    },

    setSecret(value) {
      store.patch({ secret: value, unlockError: null });
    },

    unlock() {
      const secret = store.state.secret;
      if (!secret || store.state.unlocking) return;
      store.patch({ unlocking: true, unlockError: null });
      // The secret is proved by using it. `/api/auth/me` answers `operator` to
      // a valid header and 401 to a wrong one, which is the same 401 every
      // guarded call would give; on a host without the accounts routes the
      // overview is the cheapest guarded read, and it is tried next.
      api.secret = secret;
      void api
        .me()
        .then(async (principal) => {
          if (principal?.kind === 'operator') return principal;
          if (principal === null) {
            await api.overview();
            return { kind: 'operator' } as const;
          }
          // A valid secret is answered as the operator whatever cookie the
          // browser carries — see principalOf on the server. Anything else
          // means the header was not accepted.
          throw new ApiError('the host did not accept this value as the reload secret', 401);
        })
        .then((principal) => {
          store.patch({ unlocking: false, unlockError: null, secret: '', principal });
          landAfterSignIn();
        })
        .catch((err: unknown) => {
          api.secret = '';
          store.patch({ unlocking: false, unlockError: message(err) });
        });
    },

    forget() {
      signedOut('The secret is forgotten. Nothing was sent to the server.');
    },

    checkout(plan) {
      if (store.state.billingBusy) return;
      store.patch({ billingBusy: 'checkout', billingError: null });
      // The page never composes a Stripe URL: it POSTs, and leaves for the one
      // the server returned.
      void api
        .checkout(plan)
        .then((url) => {
          location.assign(url);
        })
        .catch((err: unknown) => {
          if (unauthorized(err)) return;
          store.patch({ billingBusy: null, billingError: message(err) });
        });
    },

    signOutElsewhere() {
      securityCall('logout-all', () => api.logoutAll(), (ended) => {
        store.patch({
          notice:
            ended === 0
              ? 'No other session was open. This one is the only place you are signed in.'
              : `Signed out of ${ended} other ${ended === 1 ? 'session' : 'sessions'}. This one stays signed in.`,
        });
      });
    },

    startTwoFactor() {
      securityCall('setup', () => api.twoFactorSetup(), (enrolment) => {
        patchSecurity({ enrolment, code: '', pending: null });
      });
    },

    cancelTwoFactor() {
      // An abandoned enrolment leaves an unconfirmed secret on the server,
      // which is harmless: it is never asked for until it is confirmed, and the
      // next "Turn on" replaces it.
      patchSecurity({ enrolment: null, pending: null, code: '', error: null });
    },

    setSecurityCode(value) {
      patchSecurity({ code: value, error: null });
    },

    confirmTwoFactor() {
      const code = store.state.security.code.replace(/\s+/g, '');
      if (!code) return;
      securityCall('confirm', () => api.twoFactorConfirm(code), (recoveryCodes) => {
        patchSecurity({ enrolment: null, code: '', recoveryCodes });
        void loadSecurity();
      });
    },

    askSecurityCode(purpose) {
      patchSecurity({ pending: purpose, code: '', error: null });
    },

    submitSecurityCode() {
      const { pending } = store.state.security;
      const code = store.state.security.code.trim();
      if (!pending || !code) return;
      if (pending === 'disable') {
        securityCall('disable', () => api.twoFactorDisable(code), () => {
          patchSecurity({ pending: null, code: '', recoveryCodes: null });
          store.patch({ notice: 'Two-step sign-in is off. Your other sessions were signed out.' });
          void loadSecurity();
        });
        return;
      }
      securityCall('recovery', () => api.twoFactorRecoveryCodes(code), (recoveryCodes) => {
        patchSecurity({ pending: null, code: '', recoveryCodes });
        void loadSecurity();
      });
    },

    dismissRecoveryCodes() {
      patchSecurity({ recoveryCodes: null });
    },

    openPortal() {
      if (store.state.billingBusy) return;
      store.patch({ billingBusy: 'portal', billingError: null });
      void api
        .portal()
        .then((url) => {
          location.assign(url);
        })
        .catch((err: unknown) => {
          if (unauthorized(err)) return;
          store.patch({ billingBusy: null, billingError: message(err) });
        });
    },

    go(screen) {
      navigate({
        screen,
        usageDays: store.state.usageDays,
        usageTenantId: store.state.usageTenantId,
      });
    },

    openTenant(id, tab) {
      navigate({ screen: 'tenant', tenantId: id, tenantTab: tab ?? 'overview' });
    },

    goTenantTab(tab: TenantTab) {
      const id = store.state.tenantId;
      if (!id) return;
      navigate({ screen: 'tenant', tenantId: id, tenantTab: tab });
    },

    setTheme(choice) {
      applyTheme(choice);
      store.patch({ theme: choice });
    },

    setSiteFilter(value) {
      store.patch({ siteFilter: value });
    },

    reload() {
      loadFor(store.state.screen);
    },

    setUsageDays(days) {
      navigate({ screen: 'traffic', usageDays: days, usageTenantId: store.state.usageTenantId });
    },

    setUsageTenant(id) {
      navigate({ screen: 'traffic', usageDays: store.state.usageDays, usageTenantId: id });
    },

    editTool(tool, patch) {
      const cap = store.state.tenant?.capabilities.find((c) => c.name === tool);
      if (!cap) return;
      const next = { ...draftOf(store.state, cap), ...patch };
      store.patchMap('drafts', tool, next);
      store.patchMap(
        'draftErrors',
        tool,
        next.description.trim().length < DESCRIPTION_MIN
          ? `The manifest requires at least ${DESCRIPTION_MIN} characters.`
          : null,
      );
    },

    saveTool(tool) {
      const cap = store.state.tenant?.capabilities.find((c) => c.name === tool);
      if (!cap) return;
      const draft = draftOf(store.state, cap);
      if (draft.description.trim().length < DESCRIPTION_MIN) {
        store.patchMap('toolErrors', tool, `The manifest requires at least ${DESCRIPTION_MIN} characters.`);
        return;
      }
      void patchTool(tool, { title: draft.title, description: draft.description }).then(() => {
        // Dropped only on success: a failed save keeps the operator's text.
        if (!store.state.toolErrors[tool]) store.patchMap('drafts', tool, null);
      });
    },

    resetTool(tool) {
      store.patchMap('drafts', tool, null);
      store.patchMap('draftErrors', tool, null);
      store.patchMap('toolErrors', tool, null);
    },

    setSurface(tool, surface, on) {
      void patchTool(tool, surface === 'web' ? { web: on } : { app: on });
    },

    setVisibility(tool, target, on) {
      const cap = store.state.tenant?.capabilities.find((c) => c.name === tool);
      if (!cap) return;
      const current = cap.surfaces?.app?.visibility ?? ['model', 'app'];
      const next = on ? [...new Set([...current, target])] : current.filter((v) => v !== target);
      if (!next.length) {
        // The route requires at least one audience. Refusing here says which
        // rule was hit, instead of spending a round trip to be told the array
        // is empty by a schema.
        store.patchMap(
          'toolErrors',
          tool,
          'MCP Apps visibility needs at least one of model or app. Turn the MCP Apps surface off instead.',
        );
        return;
      }
      void patchTool(tool, { visibility: next as Array<'model' | 'app'> });
    },

    setTtl(tool, ttlSec) {
      void patchTool(tool, { ttlSec });
    },

    setRelay(tool, on) {
      // No client-side rule about WHICH forms may relay: the manifest re-parse
      // behind this PATCH refuses a third-party tenant and a captcha'd form with
      // its own sentence, and a second copy of that rule here would be the one
      // that goes stale. What comes back is printed verbatim.
      void patchTool(tool, { relay: on });
    },

    setRunArg(tool, name, value) {
      const run = runOf(store.state, tool);
      store.patchMap('runs', tool, { ...run, args: { ...run.args, [name]: value } });
    },

    runTool(tool) {
      const id = tenantId();
      const cap = store.state.tenant?.capabilities.find((c) => c.name === tool);
      if (!id || !cap) return;
      const run = runOf(store.state, tool);
      if (run.running) return;

      const fields = fieldsOf(cap.inputSchema);
      const missing = missingRequired(fields, run.args);
      if (missing.length) {
        store.patchMap('runs', tool, {
          ...run,
          error: `Fill the required ${missing.length === 1 ? 'parameter' : 'parameters'}: ${missing.join(', ')}.`,
        });
        return;
      }

      store.patchMap('runs', tool, { ...run, running: true, error: null });
      void api
        .runTool(id, tool, coerceArgs(fields, run.args))
        .then((result) => {
          store.patchMap('runs', tool, { ...runOf(store.state, tool), running: false, result, error: null });
        })
        .catch((err: unknown) => {
          if (unauthorized(err)) return;
          // The request itself failed — a 429 from the tenant's own rate limit,
          // a dead origin, a bad secret. It is printed as the error it is; an
          // empty result panel here would read as "the tool returned nothing".
          store.patchMap('runs', tool, {
            ...runOf(store.state, tool),
            running: false,
            result: null,
            error: message(err),
          });
        });
    },

    clearRun(tool) {
      const run = runOf(store.state, tool);
      store.patchMap('runs', tool, { ...run, result: null, error: null });
    },

    setConfirm(key, value) {
      store.patchMap('confirms', key, value);
    },

    deleteTool(tool) {
      const id = tenantId();
      if (!id) return;
      store.patchMap('savingTool', tool, true);
      store.patchMap('toolErrors', tool, null);
      void api
        .deleteTool(id, tool)
        .then(async () => {
          store.patchMap('confirms', `tool:${tool}`, null);
          store.patchMap('drafts', tool, null);
          store.patchMap('runs', tool, null);
          await refreshTenant();
          store.patch({ notice: `${tool} is gone from the manifest on disk.` });
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patchMap('toolErrors', tool, message(err));
        })
        .finally(() => store.patchMap('savingTool', tool, null));
    },

    editView(viewId, patch) {
      const view = store.state.tenant?.views.find((v) => v.id === viewId);
      if (!view) return;
      store.patchMap('viewDrafts', viewId, { ...viewDraftOf(store.state, view), ...patch });
    },

    editSlot(viewId, slot, value) {
      const view = store.state.tenant?.views.find((v) => v.id === viewId);
      if (!view) return;
      const draft = viewDraftOf(store.state, view);
      store.patchMap('viewDrafts', viewId, { ...draft, slots: { ...draft.slots, [slot]: value } });
    },

    saveView(viewId) {
      const id = tenantId();
      const view = store.state.tenant?.views.find((v) => v.id === viewId);
      if (!id || !view) return;
      const draft = viewDraftOf(store.state, view);

      store.patchMap('savingView', viewId, true);
      store.patchMap('viewErrors', viewId, null);
      void api
        .patchView(id, viewId, {
          slots: draft.slots,
          badges: draft.badges
            .split(',')
            .map((b) => b.trim())
            .filter(Boolean),
          // An empty box means "no link field", which the route spells as null.
          linkField: draft.linkField.trim() || null,
        } as Parameters<typeof api.patchView>[2])
        .then(async () => {
          await refreshTenant();
          store.patchMap('viewDrafts', viewId, null);
          // Said plainly rather than silently dropped: the route accepts slots,
          // badges, linkField and emptyText, and a layout change that vanished
          // on the next repaint would look like the save had failed at random.
          if (draft.layout !== view.layout || draft.title !== (view.title ?? '')) {
            store.patchMap(
              'viewErrors',
              viewId,
              'Slots, badges and the link field were saved. Layout and title are not editable through this route — they are set when the manifest is published.',
            );
          } else {
            store.patch({ notice: `Saved view ${viewId}.` });
          }
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patchMap('viewErrors', viewId, message(err));
        })
        .finally(() => store.patchMap('savingView', viewId, null));
    },

    editProxyHosts(value) {
      store.patch({ proxyDraft: value, proxyError: null });
    },

    saveProxyHosts() {
      const id = tenantId();
      const draft = store.state.proxyDraft;
      if (!id || draft === null || store.state.savingProxy) return;
      const hosts = draft
        .split(/[\s,]+/)
        .map((h) => h.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, ''))
        .filter(Boolean);
      store.patch({ savingProxy: true, proxyError: null });
      void api
        .patchIntegration(id, hosts)
        .then(async () => {
          await refreshTenant();
          store.patch({
            savingProxy: false,
            proxyDraft: null,
            notice: hosts.length ? `Declared ${hosts.join(', ')} as a proxy for /mcp.` : 'No proxy host is declared any more.',
          });
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patch({ savingProxy: false, proxyError: message(err) });
        });
    },

    editCss(value) {
      store.patch({ cssDraft: value, cssError: null });
    },

    saveCss() {
      const id = tenantId();
      const draft = store.state.cssDraft;
      if (!id || draft === null || store.state.savingCss) return;
      store.patch({ savingCss: true, cssError: null });
      void api
        .patchBrand(id, draft.trim() ? draft : null)
        .then(async () => {
          await refreshTenant();
          store.patch({
            savingCss: false,
            cssDraft: null,
            notice: draft.trim() ? 'Saved the widget stylesheet. Chat clients pick it up the next time they load a widget.' : 'Removed the widget stylesheet.',
          });
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patch({ savingCss: false, cssError: message(err) });
        });
    },

    resetCss() {
      store.patch({ cssDraft: null, cssError: null });
    },

    checkHealth() {
      const id = tenantId();
      if (!id || store.state.checking) return;
      store.patch({ checking: true, dangerError: null });
      void api
        .checkHealth(id)
        .then(async () => {
          // The probe's own reply carries the verdict and the per-tool rows but
          // no "last checked" stamp; the tenant read has it, recorded by the
          // monitor. Reloading is how that column stops saying "never" the
          // moment after a probe actually ran.
          await refreshTenant();
          store.patch({ notice: 'Probe finished. Every tool below was called against the live origin.' });
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patch({ dangerError: message(err) });
        })
        .finally(() => store.patch({ checking: false }));
    },

    issueProofs() {
      const id = tenantId();
      if (!id || store.state.issuingProofs) return;
      store.patch({ issuingProofs: true, ownershipError: null });
      void api
        .ownershipChallenge(id)
        .then((proofs) => {
          // The result is dropped, not merged: a fresh challenge retires the
          // previous token, so an attempt list from the old one would be a
          // record of a check against a string that no longer counts.
          store.patch({ proofs, proofResult: null });
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patch({ ownershipError: message(err) });
        })
        .finally(() => store.patch({ issuingProofs: false }));
    },

    checkClaim() {
      const id = tenantId();
      if (!id || store.state.checkingClaim) return;
      /**
       * Re-check when a claim is on record, verify otherwise.
       *
       * They are different routes because they are different acts: `verify`
       * answers an open challenge and mints a claim, `recheck` re-reads the
       * proof behind a claim that already exists and is the whole of the
       * renewal path. A person pressing "Check now" means the same thing by it
       * either way, so the screen picks.
       *
       * A claim that has DRIFTED off its tenant is the one claim a re-check
       * cannot help: the server answers `origin_moved` because the proof is
       * about an origin this site no longer serves, and the only way through is
       * a new challenge. So drift routes to verify, which is what the open
       * challenge behind that state is for.
       */
      const claim = store.state.tenant?.ownership ?? null;
      const proofs = store.state.proofs;
      const renewing =
        claim != null && claim.state !== 'revoked' && !claim.originDrift && !proofs;
      store.patch({ checkingClaim: true, ownershipError: null });
      const call = renewing
        ? api.ownershipRecheck(id)
        : api.ownershipVerify(id, proofs?.challengeId);
      void call
        .then(async (proofResult) => {
          store.patch({ proofResult });
          // The claim on the screen is the server's, not this answer's: a
          // verification changes what the tenant payload says about currency
          // and expiry, and those are the fields the panel branches on.
          await refreshTenant();
          if (proofResult.verified) {
            store.patch({
              notice: `${id} is verified. It may be published under this account with no disclaimer; nothing was published by the check.`,
            });
          }
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patch({ ownershipError: message(err) });
        })
        .finally(() => store.patch({ checkingClaim: false }));
    },

    unpublish() {
      const id = tenantId();
      if (!id || store.state.unpublishing) return;
      store.patch({ unpublishing: true, dangerError: null });
      void api
        .unpublish(id)
        .then(() => {
          store.patch({
            unpublishing: false,
            tenant: null,
            tenantId: null,
            notice: `${id} is unpublished. /t/${id} and /t/${id}/mcp no longer answer.`,
          });
          store.clearTenantWork();
          navigate({ screen: 'sites' });
        })
        .catch((err: unknown) => {
          if (!unauthorized(err)) store.patch({ unpublishing: false, dangerError: message(err) });
        });
    },

    /* ── Onboard ─────────────────────────────────────────────────────── */

    setOnboardUrl(value) {
      store.patchOnboard({ url: value, startError: null });
    },

    setOnboardOwnership(value) {
      store.patchOnboard({ ownership: value });
    },

    startOnboard() {
      const { url, ownership, starting } = store.state.onboard;
      const trimmed = url.trim();
      if (!trimmed || starting) return;
      store.patchOnboard({ starting: true, startError: null });
      // The body goes as typed, scheme or not: the server's URL guard is the
      // one that decides what is crawlable, and it says why in a sentence.
      void api
        .startOnboard(trimmed, ownership)
        .then((receipt) => {
          store.patchOnboard({ starting: false });
          if (receipt.deduplicated) {
            store.patch({
              notice: `A crawl of ${trimmed} is already ${receipt.state}; you have joined it rather than starting a second one.`,
            });
          } else if (receipt.note) {
            store.patch({ notice: receipt.note });
          }
          navigate({ screen: 'crawl', jobId: receipt.jobId });
        })
        .catch((err: unknown) => {
          if (unauthorized(err)) return;
          store.patchOnboard({ starting: false, startError: message(err) });
        });
    },

    openJob(jobId) {
      navigate({ screen: 'crawl', jobId });
    },

    publishDraft() {
      const { jobId, publishing } = store.state.onboard;
      if (!jobId || publishing) return;
      store.patchOnboard({ publishing: true, publishError: null });
      void api
        .publishJob(jobId)
        .then(async (published) => {
          if (store.state.onboard.jobId !== jobId) return;
          store.patchOnboard({ publishing: false, published, publishError: null });
          // The record now carries `publishedAt`, and the list's row for it
          // stops saying "draft". Both are re-read rather than edited here.
          try {
            const job = await api.onboardJob(jobId);
            if (store.state.onboard.jobId === jobId) store.patchOnboard({ job });
          } catch (err) {
            if (unauthorized(err)) return;
          }
          void loadJobs();
          // The fleet is stale by one tenant, and so is the principal's
          // membership list; the next visit to either re-reads them.
          store.patch({ overview: null });
        })
        .catch((err: unknown) => {
          if (unauthorized(err)) return;
          if (store.state.onboard.jobId !== jobId) return;
          store.patchOnboard({
            publishing: false,
            publishError: {
              message: message(err),
              code: err instanceof ApiError ? err.code : '',
            },
          });
        });
    },

    cancelCrawl() {
      const { jobId, cancelling } = store.state.onboard;
      if (!jobId || cancelling) return;
      store.patchOnboard({ cancelling: true });
      void api
        .cancelJob(jobId)
        .then((job) => {
          if (store.state.onboard.jobId !== jobId) return;
          store.patchOnboard({ cancelling: false, job });
          // The route answers the record as it stands — cancel-requested on a
          // running job, cancelled on a queued one — and the poll reads the
          // rest as the worker gets to it.
        })
        .catch((err: unknown) => {
          if (unauthorized(err)) return;
          if (store.state.onboard.jobId !== jobId) return;
          store.patchOnboard({ cancelling: false, jobError: message(err) });
        });
    },

    newCrawl() {
      navigate({ screen: 'crawl', jobId: null });
    },

    copy(id, text) {
      void copyText(text).then((ok) => {
        if (!ok) return;
        store.patch({ copied: id });
        if (copyTimer) clearTimeout(copyTimer);
        copyTimer = setTimeout(() => store.patch({ copied: null }), 1800);
      });
    },

    dismissNotice() {
      store.patch({ notice: null });
    },
  };

  store.subscribe(paint);

  /**
   * Boot: ask the server who this page is, then route.
   *
   * The placeholder paints first. A cookie session that flashed the login form
   * for the round trip on every reload would teach people the session does not
   * stick, so nothing but "checking" is shown until `/api/auth/me` answers. An
   * unreachable API is stated on the login screen in so many words — it is the
   * one screen that can render without the server — and never as an empty
   * list of sites.
   */
  // The pre-paint script in dashboard.html has already stamped the root element
  // from the same key; this reads back what it chose so the control shows it.
  const theme = storedTheme();
  applyTheme(theme);
  store.patch({ booting: true, theme });
  paint();
  void api
    .me()
    .then((principal) => {
      store.patch({ booting: false, principal });
      applyRoute();
    })
    .catch((err: unknown) => {
      store.patch({ booting: false, principal: null });
      store.patch({ screen: 'login', loginNotice: `Could not reach the dashboard API at ${origin}: ${message(err)}` });
    });

  // A useful handle during an incident, and the only global this app defines.
  // The secret is reachable through it, which is no worse than the field it was
  // typed into: both are this page's memory and neither outlives the tab.
  (window as unknown as { __clawharborDashboard?: unknown }).__clawharborDashboard = {
    store,
    api,
    actions,
  };
}

function start(): void {
  const container = document.getElementById('dashboard');
  if (!container) {
    console.error('[clawharbor/dashboard] no #dashboard container in the document');
    return;
  }
  // Drop the pre-boot placeholder before Preact takes over the subtree.
  container.replaceChildren();
  boot(container);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
